import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  decideApproval, fetchApproval, fetchApprovalPdf, fetchApprovalStatus, fetchApproverOptions, requestReroute, signInForApproval, signInRequired,
  type ApprovalLine, type ApprovalPage as ApprovalPageData, type ApproverOption,
} from "@/api/approvals";
import { errorMessage } from "@/api/closeReview";
import { GOOGLE_HOSTED_DOMAIN } from "@/config";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usd = (n: number | null) => (n == null ? "" : USD.format(n));
const day = (d: string | null) => (d ? dayjs(d).format("MMM D, YYYY") : "");

// Where an approver lands from the approval email. Approvers are mostly not app
// users: they sign in with Google right here, and the server checks the account
// is this bill's approver before showing or deciding anything.
export function ApprovalPage() {
  const { token = "" } = useParams();
  const queryClient = useQueryClient();
  const key = ["bill-approval", token];
  const query = useQuery({ queryKey: key, queryFn: () => fetchApproval(token), retry: false });
  const [signInError, setSignInError] = useState<string | null>(null);

  const signIn = useMutation({
    mutationFn: (credential: string) => signInForApproval(token, credential),
    onSuccess: (data) => queryClient.setQueryData(key, data),
    onError: (e) => setSignInError(signInRequired(e)?.error ?? errorMessage(e)),
  });

  const needsSignIn = signInRequired(query.error);

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Box component="img" src="/logo.png" alt="Freedom Power" sx={{ height: 32 }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Bill approval</Typography>
        {query.data && <Link component={RouterLink} to="/approvals">All my approvals</Link>}
      </Stack>

      {query.isLoading && <CircularProgress />}

      {needsSignIn && (
        <Card sx={{ maxWidth: 440 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography>
                Sign in with your Freedom Power Google account
                {needsSignIn.approver_name ? ` (${needsSignIn.approver_name})` : ""} to review this bill.
              </Typography>
              {(signInError ?? needsSignIn.error) && <Alert severity="error">{signInError ?? needsSignIn.error}</Alert>}
              <Box>
                <GoogleLogin
                  hosted_domain={GOOGLE_HOSTED_DOMAIN || undefined}
                  onSuccess={(resp: CredentialResponse) => { setSignInError(null); signIn.mutate(resp.credential ?? ""); }}
                  onError={() => setSignInError("Google sign-in failed.")}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {query.isError && !needsSignIn && <Alert severity="error">{errorMessage(query.error)}</Alert>}

      {query.data && <ApprovalDetail token={token} data={query.data} />}
    </Box>
  );
}

function ApprovalDetail({ token, data }: { token: string; data: ApprovalPageData }) {
  const { approval, lines, others, live } = data;
  const [choice, setChoice] = useState<"approve" | "reject" | null>(null);
  const [rerouting, setRerouting] = useState(false);
  const queryClient = useQueryClient();

  // While a decision saves to NetSuite in the background, check every 3 seconds
  // and fold the bill's new state into the page.
  const status = useQuery({
    queryKey: ["bill-approval-status", token],
    queryFn: () => fetchApprovalStatus(token),
    enabled: !!approval.deciding,
    refetchInterval: 3000,
  });
  useEffect(() => {
    if (!status.data) return;
    queryClient.setQueryData<ApprovalPageData>(["bill-approval", token], (prev) => (prev ? { ...prev, approval: status.data } : prev));
  }, [status.data, queryClient, token]);

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="h5">{approval.vendor_name}</Typography>
            <Typography variant="h4">{usd(approval.amount)}</Typography>
            <Typography color="text.secondary">
              Invoice {approval.tranid} · dated {day(approval.trandate)} · due {day(approval.duedate)}
            </Typography>
            {approval.memo && <Typography variant="body2">{approval.memo}</Typography>}
            <Typography variant="body2" color="text.secondary">Entered by {approval.submitter_name}</Typography>
          </Stack>
        </CardContent>
      </Card>

      {approval.variance_note && <Alert severity="warning"><strong>Check the totals:</strong> {approval.variance_note}</Alert>}

      <Decision approval={approval} live={live} onChoose={setChoice} onReroute={() => setRerouting(true)} />

      <Lines lines={lines} />

      <InvoicePdf token={token} />

      {others.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Also waiting for you</Typography>
            <Stack spacing={0.5}>
              {others.map((o) => (
                <Link key={o.token} component={RouterLink} to={`/approvals/${o.token}`}>
                  {o.vendor_name} {o.tranid} · {usd(o.amount)}
                </Link>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {choice && <DecisionDialog token={token} choice={choice} onClose={() => setChoice(null)} />}
      {rerouting && <RerouteDialog token={token} onClose={() => setRerouting(false)} />}
    </Stack>
  );
}

function Decision({ approval, live, onChoose, onReroute }: {
  approval: ApprovalPageData["approval"]; live: boolean; onChoose: (c: "approve" | "reject") => void;
  onReroute: () => void;
}) {
  if (approval.status === "approved" || approval.status === "rejected") {
    return (
      <Alert severity={approval.status === "approved" ? "success" : "info"}>
        {approval.status === "approved" ? "Approved" : "Rejected"} by {approval.decided_by_name}
        {approval.decided_at ? ` on ${dayjs(approval.decided_at).format("MMM D, h:mm A")}` : ""}
        {approval.comment ? `: ${approval.comment}` : ""}
      </Alert>
    );
  }
  if (approval.status === "closed") {
    return <Alert severity="info">This bill is no longer waiting for approval.</Alert>;
  }
  if (approval.deciding) {
    return (
      <Alert severity="info" icon={<CircularProgress size={20} />}>
        Saving your {approval.deciding.choice === "approve" ? "approval" : "rejection"} to NetSuite. This can take up to a
        minute; you can close this page and it will still be saved.
      </Alert>
    );
  }
  if (!live) {
    return <Alert severity="info">Approvals aren't live here yet. Approve this bill in Charted for now.</Alert>;
  }
  const reroute = approval.reroute;
  // The comment's own closing punctuation is dropped so the sentence doesn't end "..".
  const rerouteComment = reroute?.comment ? `: ${reroute.comment.replace(/[.!\s]+$/, "")}` : "";
  return (
    <Stack spacing={2}>
      {approval.decision_error && (
        <Alert severity="error">Your last decision wasn't saved: {approval.decision_error}</Alert>
      )}
      {reroute && (
        <Alert severity="info">
          {reroute.by_name} sent this back to {approval.submitter_name ?? "AP"}
          {reroute.requested_at ? ` on ${dayjs(reroute.requested_at).format("MMM D, h:mm A")}` : ""} to go
          to {reroute.to_name}{rerouteComment}. It stays here until AP changes the approver.
        </Alert>
      )}
      <Stack direction="row" spacing={2}>
        {/* Sent back as not theirs: no deciding it, only changing who it should go to. */}
        {!reroute && (
          <>
            <Button size="large" variant="contained" color="success" onClick={() => onChoose("approve")}>Approve</Button>
            <Button size="large" variant="outlined" color="error" onClick={() => onChoose("reject")}>Reject</Button>
          </>
        )}
        <Button size="large" variant={reroute ? "outlined" : "text"} onClick={onReroute}>
          {reroute ? "Change who it goes to" : "Not mine"}
        </Button>
      </Stack>
    </Stack>
  );
}

// "Not mine": pick who the bill should go to and why. AP is emailed and
// changes the approver in NetSuite; nothing changes on the bill here.
function RerouteDialog({ token, onClose }: { token: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [to, setTo] = useState<ApproverOption | null>(null);
  const [comment, setComment] = useState("");
  const options = useQuery({
    queryKey: ["approver-options", token, search],
    queryFn: () => fetchApproverOptions(token, search),
  });
  const send = useMutation({
    mutationFn: () => requestReroute(token, to!.netsuite_id, comment),
    onSuccess: (data) => { queryClient.setQueryData(["bill-approval", token], data); onClose(); },
  });

  return (
    <Dialog open onClose={send.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Who should approve this bill?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The person who entered the bill is emailed to change the approver in NetSuite.
          </Typography>
          <Autocomplete
            options={options.data ?? []}
            loading={options.isFetching}
            value={to}
            onChange={(_e, v) => setTo(v)}
            onInputChange={(_e, v) => setSearch(v)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.netsuite_id === b.netsuite_id}
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Should go to" autoFocus />}
          />
          <TextField label="Why? (sent to AP)" multiline minRows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          {send.isError && <Alert severity="error">{errorMessage(send.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={send.isPending}>Cancel</Button>
        <Button variant="contained" onClick={() => send.mutate()} disabled={send.isPending || !to || !comment.trim()}>
          {send.isPending ? "Sending..." : "Send back to AP"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DecisionDialog({ token, choice, onClose }: { token: string; choice: "approve" | "reject"; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const approve = choice === "approve";
  const decide = useMutation({
    mutationFn: () => decideApproval(token, choice, comment),
    onSuccess: (data) => { queryClient.setQueryData(["bill-approval", token], data); onClose(); },
  });

  return (
    <Dialog open onClose={decide.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{approve ? "Approve this bill?" : "Reject this bill?"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!approve && (
            <TextField label="What's wrong? (sent to the person who entered it)" multiline minRows={3} autoFocus
              value={comment} onChange={(e) => setComment(e.target.value)} />
          )}
          {decide.isError && <Alert severity="error">{errorMessage(decide.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={decide.isPending}>Cancel</Button>
        <Button variant="contained" color={approve ? "success" : "error"} onClick={() => decide.mutate()}
          disabled={decide.isPending || (!approve && !comment.trim())}>
          {decide.isPending ? (approve ? "Approving..." : "Rejecting...") : approve ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Lines({ lines }: { lines: ApprovalLine[] | null }) {
  if (lines === null) return <Alert severity="warning">Couldn't load the bill's lines from NetSuite. Refresh to try again.</Alert>;
  if (lines.length === 0) return null;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item / account</TableCell><TableCell>Memo</TableCell><TableCell align="right">Qty</TableCell>
            <TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell>
            <TableCell>Project</TableCell><TableCell>Class / Dept / Location</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((l) => (
            <TableRow key={l.line}>
              <TableCell>{l.item ?? l.account}</TableCell>
              <TableCell>{l.memo}</TableCell>
              <TableCell align="right">{l.quantity ?? ""}</TableCell>
              <TableCell align="right">{usd(l.rate)}</TableCell>
              <TableCell align="right">{usd(l.amount)}</TableCell>
              <TableCell>{l.project}</TableCell>
              <TableCell>{[l.class_name, l.department, l.location].filter(Boolean).join(" / ")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// The invoice PDF: the app's copy for bills it posted, else the one attached to
// the bill in NetSuite (Charted's invoice document or the Files subtab).
function InvoicePdf({ token }: { token: string }) {
  const pdf = useQuery({ queryKey: ["bill-approval-pdf", token], queryFn: () => fetchApprovalPdf(token), retry: false });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pdf.data) return undefined;
    const objectUrl = URL.createObjectURL(pdf.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pdf.data]);

  if (pdf.isLoading) return <Typography variant="body2" color="text.secondary">Loading the invoice PDF…</Typography>;
  if (pdf.isError) return <Alert severity="warning">Couldn't load the invoice PDF from NetSuite. Refresh to try again.</Alert>;
  if (pdf.data === null) return <Alert severity="info">No invoice PDF is attached to this bill in NetSuite.</Alert>;
  if (!url) return null;
  return (
    <Box component="iframe" title="Invoice PDF" src={url}
      sx={{ width: "100%", height: 720, border: 1, borderColor: "divider", borderRadius: 1 }} />
  );
}
